import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { caseService, documentService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";

interface EvidenciaArchivo {
  id: string;
  nombre: string;
  tipo: "pdf" | "imagen" | "documento" | "otro";
  casoId: string;
  casoFolio: string;
  subidoPor: string;
  fechaSubida: string;
  url: string;
}

export default function EvidenciasPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("todos"); // todos, pdf, imagen, documento
  const [archivos, setArchivos] = useState<EvidenciaArchivo[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [fileType, setFileType] = useState<"pdf" | "imagen" | "documento" | "otro">("pdf");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await documentService.listEvidencias();
      if (Array.isArray(data)) {
        setArchivos(data.map((d: any) => {
          const ext = d.nombre.split(".").pop()?.toLowerCase();
          let tipo: EvidenciaArchivo["tipo"] = "otro";
          if (ext === "pdf" || d.tipo === "pdf") tipo = "pdf";
          else if (["jpg", "jpeg", "png", "webp", "imagen"].includes(ext || "") || d.tipo === "imagen") tipo = "imagen";
          else if (["docx", "doc", "odt", "txt", "documento"].includes(ext || "") || d.tipo === "documento") tipo = "documento";

          return {
            id: d.id,
            nombre: d.nombre,
            tipo,
            casoId: d.caso_id,
            casoFolio: d.caso_folio || "CASO",
            subidoPor: d.subido_por_nombre || "Profesional",
            fechaSubida: d.created_at ? new Date(d.created_at).toLocaleDateString("es-CL") : new Date().toLocaleDateString("es-CL"),
            url: d.url
          };
        }));
      } else {
        throw new Error("Invalid list format received from API");
      }
    } catch (err: any) {
      console.error("Error loading evidences:", err);
      setError("Error al cargar evidencias desde la base de datos de Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadCases = async () => {
    try {
      const data = await caseService.list();
      if (Array.isArray(data)) {
        setCases(data.map(mapDbCaseToFrontend));
      }
    } catch (err) {
      console.error("Error loading cases for selection:", err);
    }
  };

  useEffect(() => {
    loadData();
    loadCases();
  }, []);

  const filtered = useMemo(() => {
    return archivos.filter((item) => {
      if (tipoFilter !== "todos" && item.tipo !== tipoFilter) {
        return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          item.nombre.toLowerCase().includes(q) ||
          item.subidoPor.toLowerCase().includes(q) ||
          (item.casoFolio && item.casoFolio.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [archivos, search, tipoFilter]);

  const handleDownload = async (file: EvidenciaArchivo) => {
    try {
      const downloadUrl = await documentService.getDownloadUrl("evidencias", file.url);
      window.open(downloadUrl, "_blank");
    } catch (err: any) {
      alert("Error al descargar archivo de Azure Storage: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenUpload = () => {
    setSelectedCaseId("");
    setSelectedFile(null);
    setFileType("pdf");
    setShowUploadModal(true);
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCaseId) {
      alert("Por favor seleccione un caso relacionado.");
      return;
    }
    if (!selectedFile) {
      alert("Por favor seleccione un archivo para subir.");
      return;
    }

    const formData = new FormData();
    formData.append("caso_id", selectedCaseId);
    formData.append("tipo", fileType);
    formData.append("file", selectedFile);

    setUploading(true);
    try {
      await documentService.uploadEvidencia(formData);
      setShowUploadModal(false);
      setSelectedFile(null);
      await loadData();
    } catch (err: any) {
      alert("Error al subir archivo de evidencia a Azure Storage: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const getIconClass = (tipo: EvidenciaArchivo["tipo"]) => {
    switch (tipo) {
      case "pdf":
        return "ri-file-pdf-line text-red-500 bg-red-50";
      case "imagen":
        return "ri-image-line text-emerald-500 bg-emerald-50";
      case "documento":
        return "ri-file-word-line text-blue-500 bg-blue-50";
      default:
        return "ri-file-line text-foreground-500 bg-background-50";
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">Bóveda de Evidencias</h1>
          <p className="text-sm text-foreground-500 mt-1">Expedientes y documentos probatorios adjuntos a los casos e de la base de datos de Azure.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleOpenUpload}
            className="flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600 cursor-pointer"
            size="md"
          >
            <i className="ri-upload-cloud-line" />
            Adjuntar Evidencia
          </Button>
          <button
            onClick={() => navigate("/convivencia/casos")}
            className="flex items-center gap-2 px-4 py-2.5 border border-background-200 rounded-xl text-sm font-medium text-foreground-600 hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap bg-white"
          >
            <i className="ri-folder-3-line" />
            Ver Casos
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, folio de caso, responsable..."
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-sm outline-none focus:border-primary-300 transition-all"
          />
        </div>
        <select
          value={tipoFilter}
          onChange={(e) => setTipoFilter(e.target.value)}
          className="px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-700 outline-none cursor-pointer"
        >
          <option value="todos">Todos los formatos</option>
          <option value="pdf">PDF (.pdf)</option>
          <option value="imagen">Imágenes (.jpg, .png)</option>
          <option value="documento">Documentos (.docx, .doc)</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-background-200 rounded-2xl p-12 text-center shadow-xs">
          <i className="ri-file-shield-2-line text-5xl text-foreground-200 mb-4 block" />
          <p className="text-base font-medium text-foreground-600">Bóveda vacía o sin coincidencias</p>
          <p className="text-sm text-foreground-400 mt-1">No se encontraron archivos de evidencias en Azure PostgreSQL para este establecimiento</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {filtered.map((file) => (
            <div
              key={file.id}
              className="bg-white border border-background-200 rounded-2xl p-4 hover:shadow-soft transition-all duration-200 flex flex-col justify-between shadow-xs"
            >
              <div className="flex gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${getIconClass(file.tipo)}`}>
                  <i className="ri-file-text-line" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 truncate" title={file.nombre}>
                    {file.nombre}
                  </p>
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    Subido: {file.fechaSubida} · Por: {file.subidoPor}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between text-[11px]">
                <div className="min-w-0">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider font-bold">
                    Caso Relacionado
                  </p>
                  <p className="text-foreground-700 truncate font-mono mt-0.5">
                    {file.casoFolio || "—"}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(file)}
                  className="w-8 h-8 rounded-lg hover:bg-background-50 border border-background-200 flex items-center justify-center cursor-pointer text-foreground-600 hover:text-primary-500 transition-colors"
                >
                  <i className="ri-download-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL: SUBIR / ADJUNTAR EVIDENCIA ── */}
      {showUploadModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowUploadModal(false)}>
          <form onSubmit={handleUploadSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Adjuntar Archivo de Evidencia</h2>
              <button type="button" onClick={() => setShowUploadModal(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Archivo a Subir</label>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                  accept=".pdf,.png,.jpg,.jpeg,.docx,.doc,.txt"
                  className="w-full text-xs text-foreground-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Caso Relacionado</label>
                <select
                  value={selectedCaseId}
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                  required
                >
                  <option value="">Seleccione el caso...</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>{c.studentName} - {c.folio} ({c.titulo.substring(0, 20)}...)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tipo de Documento</label>
                <select
                  value={fileType}
                  onChange={(e: any) => setFileType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="pdf">Documento PDF (.pdf)</option>
                  <option value="imagen">Captura o Imagen (.png, .jpg)</option>
                  <option value="documento">Texto o Word (.docx, .txt)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white cursor-pointer"
                loading={uploading}
              >
                Subir a Azure
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}